"use client";

import { markdownComponents } from "@repo/ui/ai-elements/markdown-components";
import { Shimmer } from "@repo/ui/ai-elements/shimmer";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui/tooltip";
import { Button } from "@repo/ui/ui/button";
import { ButtonGroup, ButtonGroupText } from "@repo/ui/ui/button-group";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/ui/dialog";
import type { FileUIPart, UIMessage } from "ai";
import type { ComponentProps, HTMLAttributes, ReactElement } from "react";
import { createContext, memo, useContext, useEffect, useState } from "react";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { defaultRehypePlugins, Streamdown } from "streamdown";

export type {
  Components as StreamdownComponents,
  UrlTransform,
} from "streamdown";
/**
 * Re-export Streamdown's URL sanitizer + types so consumers in app workspaces
 * can extend {@link MessageResponse} (e.g. add a `components.a` override) without
 * adding `streamdown` as a direct dependency.
 */
export { defaultUrlTransform } from "streamdown";

/**
 * Streamdown 2.5.0 sanitizes anchors via `rehype-sanitize`'s `defaultSchema`
 * before its `rehype-harden` step runs. That schema only permits a fixed set
 * of `href` protocols, so the supervisor's private `mention:` scheme (used to
 * render AI-emitted entity chips — see `docs/agents/chat-mentions.md`) gets
 * stripped and the link is then hardened into a "[blocked]" span. This schema
 * re-permits `mention:` (plus Streamdown's own `tel` protocol and the
 * `metastring` code attribute used for code-fence meta) so the href survives
 * to the `components.a` override that swaps it for a chip.
 */
const mentionSanitizeSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), "tel", "mention"],
  },
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), "metastring"],
  },
};

/**
 * Streamdown's default rehype pipeline with the sanitize step swapped for
 * {@link mentionSanitizeSchema}. `rehype-harden`'s `allowedProtocols` default
 * is already `["*"]`, so once sanitize stops stripping the href, `mention:`
 * links pass through unchanged.
 */
const mentionRehypePlugins = [
  defaultRehypePlugins.raw,
  [rehypeSanitize, mentionSanitizeSchema] as [
    typeof rehypeSanitize,
    typeof mentionSanitizeSchema,
  ],
  defaultRehypePlugins.harden,
].filter((plugin): plugin is NonNullable<typeof plugin> => plugin != null);

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full max-w-[95%] flex-col gap-2",
      from === "user" ? "is-user ml-auto justify-end" : "is-assistant",
      className,
    )}
    {...props}
  />
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({
  children,
  className,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(
      "is-user:dark flex w-fit min-w-0 max-w-full flex-col gap-2 overflow-hidden text-sm",
      "group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground",
      "group-[.is-assistant]:text-foreground",
      // Override the default styles
      "w-full text-xs group-[.is-user]:w-fit group-[.is-user]:px-3 group-[.is-user]:py-2",
      // Compact markdown lists inside user bubbles
      "group-[.is-user]:**:data-[streamdown=list-item]:py-0 group-[.is-user]:**:data-[streamdown=ordered-list]:pl-4 group-[.is-user]:**:data-[streamdown=unordered-list]:pl-4",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export type MessageActionsProps = ComponentProps<"div">;

export const MessageActions = ({
  className,
  children,
  ...props
}: MessageActionsProps) => (
  <div className={cn("flex items-center gap-1", className)} {...props}>
    {children}
  </div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string;
  label?: string;
};

export const MessageAction = ({
  tooltip,
  children,
  label,
  variant = "ghost",
  size = "icon-sm",
  ...props
}: MessageActionProps) => {
  const button = (
    <Button size={size} type="button" variant={variant} {...props}>
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger render={button} />
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
};

type MessageBranchContextType = {
  currentBranch: number;
  totalBranches: number;
  goToPrevious: () => void;
  goToNext: () => void;
  branches: ReactElement[];
  setBranches: (branches: ReactElement[]) => void;
};

const MessageBranchContext = createContext<MessageBranchContextType | null>(
  null,
);

const useMessageBranch = () => {
  const context = useContext(MessageBranchContext);

  if (!context) {
    throw new Error(
      "MessageBranch components must be used within MessageBranch",
    );
  }

  return context;
};

export type MessageBranchProps = HTMLAttributes<HTMLDivElement> & {
  defaultBranch?: number;
  onBranchChange?: (branchIndex: number) => void;
};

export const MessageBranch = ({
  defaultBranch = 0,
  onBranchChange,
  className,
  ...props
}: MessageBranchProps) => {
  const [currentBranch, setCurrentBranch] = useState(defaultBranch);
  const [branches, setBranches] = useState<ReactElement[]>([]);

  const handleBranchChange = (newBranch: number) => {
    setCurrentBranch(newBranch);
    onBranchChange?.(newBranch);
  };

  const goToPrevious = () => {
    const newBranch =
      currentBranch > 0 ? currentBranch - 1 : branches.length - 1;
    handleBranchChange(newBranch);
  };

  const goToNext = () => {
    const newBranch =
      currentBranch < branches.length - 1 ? currentBranch + 1 : 0;
    handleBranchChange(newBranch);
  };

  const contextValue: MessageBranchContextType = {
    currentBranch,
    totalBranches: branches.length,
    goToPrevious,
    goToNext,
    branches,
    setBranches,
  };

  return (
    <MessageBranchContext.Provider value={contextValue}>
      <div
        className={cn("grid w-full gap-2 [&>div]:pb-0", className)}
        {...props}
      />
    </MessageBranchContext.Provider>
  );
};

export type MessageBranchContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageBranchContent = ({
  children,
  ...props
}: MessageBranchContentProps) => {
  const { currentBranch, setBranches, branches } = useMessageBranch();
  const childrenArray = Array.isArray(children) ? children : [children];

  // Use useEffect to update branches when they change
  useEffect(() => {
    if (branches.length !== childrenArray.length) {
      setBranches(childrenArray);
    }
  }, [childrenArray, branches, setBranches]);

  return childrenArray.map((branch, index) => (
    <div
      className={cn(
        "grid gap-2 overflow-hidden [&>div]:pb-0",
        index === currentBranch ? "block" : "hidden",
      )}
      key={branch.key}
      {...props}
    >
      {branch}
    </div>
  ));
};

export type MessageBranchSelectorProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};

export const MessageBranchSelector = ({
  className,
  from,
  ...props
}: MessageBranchSelectorProps) => {
  const { totalBranches } = useMessageBranch();

  // Don't render if there's only one branch
  if (totalBranches <= 1) {
    return null;
  }

  return (
    <ButtonGroup
      className="[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md"
      orientation="horizontal"
      {...props}
    />
  );
};

export type MessageBranchPreviousProps = ComponentProps<typeof Button>;

export const MessageBranchPrevious = ({
  children,
  ...props
}: MessageBranchPreviousProps) => {
  const { goToPrevious, totalBranches } = useMessageBranch();

  return (
    <Button
      aria-label="Previous branch"
      disabled={totalBranches <= 1}
      onClick={goToPrevious}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon name="chevron-left" className="size-4" />}
    </Button>
  );
};

export type MessageBranchNextProps = ComponentProps<typeof Button>;

export const MessageBranchNext = ({
  children,
  className,
  ...props
}: MessageBranchNextProps) => {
  const { goToNext, totalBranches } = useMessageBranch();

  return (
    <Button
      aria-label="Next branch"
      disabled={totalBranches <= 1}
      onClick={goToNext}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon name="chevron-right" className="size-4" />}
    </Button>
  );
};

export type MessageBranchPageProps = HTMLAttributes<HTMLSpanElement>;

export const MessageBranchPage = ({
  className,
  ...props
}: MessageBranchPageProps) => {
  const { currentBranch, totalBranches } = useMessageBranch();

  return (
    <ButtonGroupText
      className={cn(
        "border-none bg-transparent text-muted-foreground shadow-none",
        className,
      )}
      {...props}
    >
      {currentBranch + 1} of {totalBranches}
    </ButtonGroupText>
  );
};

export type MessageResponseProps = ComponentProps<typeof Streamdown> & {
  /** When true, applies tighter spacing/sizing for dense surfaces like settings cards. */
  compact?: boolean;
};

/** Props passed by Streamdown to overridden components (e.g. paragraph); includes the hast AST node. */
// type StreamdownParagraphProps = ComponentPropsWithoutRef<"p"> & {
//   node?: unknown;
// };

/** Render paragraphs as divs to avoid invalid HTML: Streamdown can put block elements (e.g. code-block fallback) inside paragraphs, and <div> cannot be a descendant of <p>. */
// function ParagraphAsDiv({
//   className,
//   children,
//   node: _node,
//   ...props
// }: StreamdownParagraphProps) {
//   return (
//     <div className={className} data-streamdown="paragraph" {...props}>
//       {children}
//     </div>
//   );
// }

export const MessageResponse = memo(
  ({ className, components, compact, ...props }: MessageResponseProps) => (
    <Streamdown
      className={cn(
        "size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
        // Streamdown ships list classes from node_modules that Tailwind doesn't scan —
        // re-declare them so the CSS is generated. Use list-outside + pl-5 so wrapped
        // text aligns under the text (not under the bullet) and bullets are indented.
        "[&_ol]:list-outside [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:pl-5",
        // Tighten bullet-to-text gap by removing default block margins on paragraphs
        // that markdown puts inside list items.
        "[&_[data-streamdown=list-item]>p]:my-0",
        // Heading scale — flatter than browser defaults so markdown sits well in the app.
        "**:data-[streamdown=heading-1]:mt-5 **:data-[streamdown=heading-1]:mb-2 **:data-[streamdown=heading-1]:font-semibold **:data-[streamdown=heading-1]:text-base",
        "**:data-[streamdown=heading-2]:mt-5 **:data-[streamdown=heading-2]:mb-2 **:data-[streamdown=heading-2]:font-semibold **:data-[streamdown=heading-2]:text-sm",
        "**:data-[streamdown=heading-3]:mt-4 **:data-[streamdown=heading-3]:mb-1.5 **:data-[streamdown=heading-3]:font-semibold **:data-[streamdown=heading-3]:text-muted-foreground **:data-[streamdown=heading-3]:text-xs **:data-[streamdown=heading-3]:uppercase **:data-[streamdown=heading-3]:tracking-wide",
        "**:data-[streamdown=heading-4]:mt-3 **:data-[streamdown=heading-4]:mb-1 **:data-[streamdown=heading-4]:font-medium **:data-[streamdown=heading-4]:text-xs",
        "**:data-[streamdown=heading-5]:font-medium **:data-[streamdown=heading-5]:text-xs",
        "**:data-[streamdown=heading-6]:font-medium **:data-[streamdown=heading-6]:text-muted-foreground **:data-[streamdown=heading-6]:text-xs",
        // Lists — tighter rhythm, snug leading, breathing room before nested lists.
        "**:data-[streamdown=ordered-list]:space-y-1 **:data-[streamdown=unordered-list]:space-y-1 **:data-[streamdown=list-item]:leading-snug",
        "[&_[data-streamdown=list-item]>[data-streamdown=ordered-list]]:mt-1 [&_[data-streamdown=list-item]>[data-streamdown=unordered-list]]:mt-1",
        // Links / emphasis / blockquotes.
        "**:data-[streamdown=link]:text-blue-600 **:data-[streamdown=link]:underline **:data-[streamdown=link]:underline-offset-2 dark:**:data-[streamdown=link]:text-blue-400",
        "**:data-[streamdown=strong]:font-semibold",
        "**:data-[streamdown=blockquote]:border-l-2 **:data-[streamdown=blockquote]:pl-3 **:data-[streamdown=blockquote]:text-muted-foreground",
        compact ? "space-y-1.5" : "space-y-2",
        compact && [
          "**:data-[streamdown=heading-1]:mt-3",
          "**:data-[streamdown=heading-2]:mt-3 **:data-[streamdown=heading-2]:mb-1",
          "**:data-[streamdown=heading-3]:mt-3 **:data-[streamdown=heading-3]:mb-1",
          "**:data-[streamdown=heading-4]:mt-2",
          "**:data-[streamdown=list-item]:text-xs",
        ],
        className,
      )}
      rehypePlugins={mentionRehypePlugins}
      components={{ ...markdownComponents, ...components }}
      {...props}
    />
  ),
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.compact === nextProps.compact,
);

MessageResponse.displayName = "MessageResponse";

export type MessageAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart;
  className?: string;
  onRemove?: () => void;
};

export function MessageAttachment({
  data,
  className,
  onRemove,
  ...props
}: MessageAttachmentProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const filename = data.filename || "";
  const mediaType =
    data.mediaType?.startsWith("image/") && data.url ? "image" : "file";
  const isImage = mediaType === "image";
  const attachmentLabel = filename || (isImage ? "Image" : "Attachment");

  return (
    <div
      className={cn(
        "group/attachment relative size-8 shrink-0 overflow-hidden rounded-md border",
        className,
      )}
      {...props}
    >
      {isImage ? (
        <>
          <button
            type="button"
            aria-label="Preview image"
            className="block size-full cursor-pointer"
            onClick={() => setPreviewOpen(true)}
          >
            <img
              alt={filename || "attachment"}
              className="size-full object-cover"
              src={data.url}
              draggable={false}
            />
          </button>
          {onRemove && (
            <button
              type="button"
              aria-label="Remove attachment"
              className="absolute top-0.5 right-0.5 flex size-3.5 items-center justify-center rounded-full bg-black/60 opacity-0 transition-opacity group-hover/attachment:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <Icon name="cross" className="size-2.5 text-white" />
            </button>
          )}
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent
              hideOverlay={false}
              className="flex max-w-3xl items-center justify-center border-0 bg-transparent p-0 ring-0"
            >
              <DialogTitle className="sr-only">{attachmentLabel}</DialogTitle>
              <img
                alt={filename || "attachment"}
                className="max-h-[85vh] w-auto rounded-lg object-contain"
                src={data.url}
              />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <>
          <Tooltip>
            <TooltipTrigger
              render={
                <div className="flex size-full items-center justify-center bg-muted text-muted-foreground" />
              }
            >
              <Icon name="link" className="size-4" />
            </TooltipTrigger>
            <TooltipContent>
              <p>{attachmentLabel}</p>
            </TooltipContent>
          </Tooltip>
          {onRemove && (
            <button
              type="button"
              aria-label="Remove attachment"
              className="absolute inset-0 flex items-start justify-end bg-black/40 p-0.5 opacity-0 transition-opacity group-hover/attachment:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <Icon name="cross" className="size-3 text-white" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export type MessageAttachmentsProps = ComponentProps<"div">;

export function MessageAttachments({
  children,
  className,
  ...props
}: MessageAttachmentsProps) {
  if (!children) {
    return null;
  }

  return (
    <div
      className={cn(
        "ml-auto flex w-fit flex-wrap items-start gap-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export type MessageToolbarProps = ComponentProps<"div">;

export const MessageToolbar = ({
  className,
  children,
  ...props
}: MessageToolbarProps) => (
  <div
    className={cn(
      "mt-4 flex w-full items-center justify-between gap-4",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export type MessageLoadingProps = HTMLAttributes<HTMLDivElement>;

/** Animated loading indicator rendered as an assistant message with shimmering text. */
export const MessageLoading = ({
  className,
  ...props
}: MessageLoadingProps) => (
  <Message from="assistant">
    <MessageContent>
      <div className={cn("py-1", className)} {...props}>
        <Shimmer className="font-medium text-xs">Running...</Shimmer>
      </div>
    </MessageContent>
  </Message>
);

export type MessageErrorProps = HTMLAttributes<HTMLDivElement> & {
  onRetry?: () => void;
};

/** Error indicator rendered as an assistant message when the AI stream fails. */
export const MessageError = ({
  className,
  onRetry,
  ...props
}: MessageErrorProps) => (
  <Message from="assistant">
    <MessageContent>
      <div
        className={cn("flex items-center gap-1.5 text-destructive", className)}
        {...props}
      >
        <Icon name="warning" className="size-3.5 shrink-0" />
        <span>Something went wrong. Please try again.</span>
      </div>
      {onRetry && (
        <div>
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        </div>
      )}
    </MessageContent>
  </Message>
);
